<template>
  <div class="station-pricing" v-loading="loading">
    <h1 class="title">My pricing</h1>

    <div v-if="!loading && rows.length === 0" class="empty">
      No prices configured for this station yet.
    </div>

    <ul v-if="rows.length > 0" class="component-list">
      <li v-for="row in rows" :key="row.id" class="component-row">
        <span class="name">{{ row.gasType ? row.gasType.name : '—' }}</span>
        <el-tag size="mini" type="info" class="source">{{ sourceLabel(row) }}</el-tag>
        <span class="price">{{ currency }} {{ formatPrice(toDecimal(row.price)) }} / L</span>
      </li>
    </ul>

    <br />

    <el-collapse v-if="!loading" v-model="openSections" class="sections">
      <el-collapse-item title="Example fills" name="examples">
        <example-fills :components="components" :currency="currency" />
      </el-collapse-item>
      <el-collapse-item title="Calculate a fill" name="calculator">
        <fill-calculator :components="components" :currency="currency" />
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script>
import ExampleFills from './ExampleFills'
import FillCalculator from './FillCalculator'
import { matchComponent } from './componentAlias'
import { deriveSource } from './deriveSource'

export default {
  name: 'StationPricing',
  components: { ExampleFills, FillCalculator },
  props: {
    station: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      loading: true,
      rows: [],
      openSections: []
    }
  },
  computed: {
    currency () {
      if (this.station.organization && this.station.organization.currency) {
        return this.station.organization.currency.name
      }
      return this.$root.currency || ''
    },
    components () {
      const map = { air: null, o2: null, he: null }
      this.rows.forEach(row => {
        if (!row.gasType) return
        const kind = matchComponent(row.gasType.name)
        if (!kind || map[kind] !== null) return
        const price = this.toDecimal(row.price)
        if (price !== null) map[kind] = price
      })
      return map
    }
  },
  methods: {
    toDecimal (cents) {
      if (cents === null || cents === undefined) return null
      const n = Number(cents)
      if (!Number.isFinite(n)) return null
      return n / 100
    },
    formatPrice (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(2)
    },
    sourceLabel (row) {
      const source = deriveSource(row)
      if (source === 'user') return 'you'
      if (source === 'group') {
        const name = row.group && row.group.name
        return name ? `group: ${name}` : 'group'
      }
      if (source === 'station') return 'station'
      return 'org default'
    },
    async fetchPrices () {
      const orgId = this.station.organization && this.station.organization.id
      if (!orgId) {
        this.loading = false
        return
      }
      try {
        const url = this.$api.organization(orgId).resolvedPricesForStation(this.$route.params.sId)
        const response = await this.$http.get(url)
        this.rows = Array.isArray(response.data) ? response.data : []
      } catch (e) {
        this.rows = []
      } finally {
        this.loading = false
      }
    }
  },
  created () {
    this.fetchPrices()
  }
}
</script>

<style lang="scss" scoped>
  .station-pricing {
    padding: 20px;
    min-height: 100px;
  }

  .empty {
    color: $text-light;
    font-size: 12px;
    text-align: center;
    padding: 20px 0;
  }

  .component-list {
    list-style: none;
    margin: 0 0 15px 0;
    padding: 0;
  }

  .component-row {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid $border;

    &:last-child {
      border-bottom: none;
    }

    .name {
      flex: 1;
      font-weight: 500;
    }

    .source {
      margin: 0 10px;
    }

    .price {
      font-variant-numeric: tabular-nums;
      color: $text-light;
      font-size: 13px;
    }
  }

  .sections {
    margin-top: 10px;
    border-top: 1px solid $border;
  }
</style>



// WEBPACK FOOTER //
// src/components/dashboard/stations/station/Pricing/StationPricing.vue